// Error-envelope parsing. Deliberately kept out of api.ts so it carries no
// dependency on Amplify — it is pure logic, independently testable, and still
// available to components whose tests factory-mock the api module.

/**
 * A server error, normalised.
 *
 * Branch on `code`, never on `message`. `message` is display copy that may be
 * reworded at any time; `code` is part of the API contract (see the Errors section
 * of docs/api.md). Matching on message text is how a client silently breaks when
 * someone edits a string — which is exactly what the desktop used to do.
 */
export interface ApiError {
  /** HTTP status, or 0 when the request never reached the server. */
  status: number;
  /** Machine-readable code. Always present. */
  code: string;
  /** Human-facing text. Safe to show; never branch on it. */
  message: string;
  /** The whole parsed body, for the few errors that carry more than the envelope. */
  body?: Record<string, unknown> | null;
}

// Mirrors the server's status-derived defaults, for responses that did not come from
// our own error helper at all — a gateway 502, a CloudFront page, the API Gateway
// authorizer's own 403, a dropped connection.
const DEFAULT_CODES: Record<number, string> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  429: 'RATE_LIMITED',
  500: 'INTERNAL_ERROR',
  501: 'NOT_IMPLEMENTED',
};

// Amplify v6 hands the error body over as TEXT (api-rest's parseRestApiServiceError
// reads it with body.text()), and copies the body's `code` into the error's name.
// This read `body.json()`, which a string does not have, so every error fell through
// to the status default: `code` was never the server's, and every branch on it was
// dead. The object form is still accepted for callers that build one.
interface AmplifyErrorLike {
  name?: string;
  message?: string;
  response?: {
    statusCode?: number;
    body?: string | { json: () => Promise<unknown> };
  };
}

/**
 * Turn a thrown Amplify API error into an `ApiError`. Safe to call on anything — a
 * network failure with no response yields `{ status: 0, code: 'NETWORK_ERROR' }`.
 */
export async function parseApiError(err: unknown): Promise<ApiError> {
  const response = (err as AmplifyErrorLike)?.response;
  const status = response?.statusCode ?? 0;

  let body: Record<string, unknown> | null = null;
  try {
    const raw = response?.body;
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw ? await raw.json() : null;
    body = parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
  } catch {
    // Non-JSON error body (gateway HTML, empty response) — fall through to defaults.
  }

  let code = typeof body?.code === 'string' ? body.code : '';
  let message = typeof body?.message === 'string' ? body.message : '';
  if (!code) code = status === 0 ? 'NETWORK_ERROR' : (DEFAULT_CODES[status] ?? 'ERROR');
  if (!message) message = (err as AmplifyErrorLike)?.message || 'Request failed. Please try again.';

  return { status, code, message, body };
}
